import React, { useState } from 'react';
import { Card, Button, Table, Badge, Spinner, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { DUYET_HOCPHANBOSUNG_API, HOCPHANBOSUNG_API, BaseGatewayImage } from '@app/utils/apiUrl';
import { trangThaiHocBoSungValue, objectTrangThaiHocBoSung } from '@app/utils/define';
import { Get, Put, DeleteSingle } from '@app/utils/httpProvider';
import moment from 'moment';
import { toast } from 'react-toastify';
import HocPhanBoSungEdit from './HocPhanBoSungEdit';
import HocPhanBoSungAdd from './HocPhanBoSungAdd';
import { ConfirmModal } from '@app/modules/modal/ConfirmModal';
import { useMutation, useQueryClient } from 'react-query';

const DuyetHocPhanBoSung = ({ hoSoId }) => {
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [dataEdit, setDataEdit] = useState(null);
    const [trangThai, setTrangThai] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');
    
    const queryClient = useQueryClient();

    const { isLoading, data: queryData, refetch } = useQuery(
        ['duyetHocPhanBoSungData', hoSoId],
        () => Get(DUYET_HOCPHANBOSUNG_API.GET_PAGING, null, { HoSoNghienCuuSinhId: hoSoId, PageIndex: 1, PageSize: 100 }),
        { 
            enabled: !!hoSoId,
            keepPreviousData: true
        }
    );

    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else if (queryData.data?.$values) {
            dataList = queryData.data.$values;
        }
    }

    const handleAction = (item) => {
        setDataEdit(item);
        setTrangThai(item.trangThai || trangThaiHocBoSungValue.chuaHoc);
        setShowModal(true);
    };

    const deleteFunc = useMutation((id) => DeleteSingle(HOCPHANBOSUNG_API.DELETE_ITEM, id, null));

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name || 'Bản ghi này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['duyetHocPhanBoSungData', hoSoId]);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTooltip = (props, text) => (
        <Tooltip id="button-tooltip" {...props}>
            {text}
        </Tooltip>
    );

    const submitDuyet = async () => {
        if (!trangThai) {
            toast.warning('Vui lòng chọn trạng thái!');
            return;
        }

        const payload = {
            HoSoNghienCuuSinhId: hoSoId,
            TenHocPhan: dataEdit.tenHocPhan || '',
            NgayBatDau: dataEdit.ngayBatDau || null,
            SoHocPhan: dataEdit.soHocPhan !== null && dataEdit.soHocPhan !== undefined ? Number(dataEdit.soHocPhan) : null,
            DiemSo: dataEdit.diemSo !== null && dataEdit.diemSo !== undefined ? Number(dataEdit.diemSo) : null,
            FileCongVan: dataEdit.fileCongVan || '',
            FileKetQua: dataEdit.fileKetQua || '',
            TrangThai: trangThai
        };

        setIsSaving(true);
        try {
            const res = await Put(DUYET_HOCPHANBOSUNG_API.DUYET_HO_SO, dataEdit.id, payload);
            if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.id || res.success)) {
                toast.success('Lưu thành công!');
                refetch();
                setShowModal(false);
            } else {
                toast.error(res?.message || 'Có lỗi xảy ra');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra');
        } finally {
            setIsSaving(false);
        }
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiHocBoSungValue.chuaHoc: return <Badge variant="warning">Chưa học</Badge>;
            case trangThaiHocBoSungValue.dangDiHoc: return <Badge variant="info">Đang đi học</Badge>;
            case trangThaiHocBoSungValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="font-weight-bold text-primary m-0">Học phần bổ sung</h5>
                    <Button variant="success" size="sm" onClick={() => setViewMode('add')}>
                        <i className="fas fa-plus mr-1"></i> HLĐT Thêm mới
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th className="text-center" width="50">STT</th>
                                    <th>Tên học phần</th>
                                    <th className="text-center">Ngày bắt đầu</th>
                                    <th className="text-center">Số học phần/Tín chỉ</th>
                                    <th className="text-center">Điểm số</th>
                                    <th className="text-center">File công văn</th>
                                    <th className="text-center">Trạng thái</th>
                                    <th className="text-center">File kết quả</th>
                                    <th className="text-center" width="120">Thao tác</th>
                                </tr>
                            </thead>
                        <tbody>
                            {dataList.length > 0 ? (
                                dataList.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>{item.tenHocPhan}</td>
                                        <td className="text-center">
                                            {item.ngayBatDau ? moment(item.ngayBatDau).format('DD/MM/YYYY') : ''}
                                        </td>
                                        <td className="text-center">{item.soHocPhan !== null && item.soHocPhan !== undefined ? item.soHocPhan : ''}</td>
                                        <td className="text-center">{item.diemSo !== null && item.diemSo !== undefined ? item.diemSo : ''}</td>
                                        <td className="text-center">
                                            {item.fileCongVan ? (
                                                (() => {
                                                    const path = item.fileCongVan;
                                                    const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                    const safePath = path.startsWith('/') ? path : `/${path}`;
                                                    const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                    let name = path.split('/').pop() || 'File';
                                                    name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                    return (
                                                        <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                                            <i className="fas fa-paperclip mr-1"></i> {name}
                                                        </a>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-muted">Không có</span>
                                            )}
                                        </td>
                                        <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                        <td className="text-center">
                                            {item.fileKetQua ? (
                                                (() => {
                                                    const path = item.fileKetQua;
                                                    const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                    const safePath = path.startsWith('/') ? path : `/${path}`;
                                                    const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                    let name = path.split('/').pop() || 'File';
                                                    name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                    return (
                                                        <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                                            <i className="fas fa-paperclip mr-1"></i> {name}
                                                        </a>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-muted">Không có</span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            {(() => {
                                                return (
                                                    <div className="d-flex justify-content-center text-nowrap">
                                                        <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Cập nhật trạng thái')}>
                                                            <Button variant="warning" size="sm" onClick={() => handleAction(item)} className="mr-1">
                                                                <i className="fas fa-tasks"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Sửa dành cho HLĐT')}>
                                                            <Button variant="info" size="sm" onClick={() => { setDataEdit(item); setViewMode('edit'); }} className="mr-1">
                                                                <i className="fas fa-edit"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Xóa')}>
                                                            <Button variant="danger" size="sm" onClick={() => deleteAction(item.id, item.tenHocPhan)}>
                                                                <i className="fas fa-trash"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center text-muted">
                                        Chưa có dữ liệu Học phần bổ sung
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    </div>
                )}
            </Card.Body>
        </Card>

        <ConfirmModal
            isOpen={openConfirmDeleteModal}
            confirmType="delete"
            goBackAfterDelete={false}
            handleAccept={acceptDelete}
            handleCancel={cancelDelete}
            content={confirmDelMessage}
        />

        {showModal && (
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false} size="md">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>Cập nhật trạng thái Học phần bổ sung</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Trạng thái</Form.Label>
                            <Form.Control 
                                as="select" 
                                value={trangThai}
                                onChange={(e) => setTrangThai(e.target.value)}
                            >
                                {objectTrangThaiHocBoSung.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                        <i className="fas fa-times mr-1"></i> Hủy
                    </Button>
                    <Button variant="primary" onClick={submitDuyet} disabled={isSaving}>
                        {isSaving ? 'Đang lưu...' : <><i className="fas fa-save mr-1"></i> Lưu lại</>}
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        
        {viewMode === 'edit' && dataEdit && (
            <HocPhanBoSungEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} />
        )}
        {viewMode === 'add' && (
            <HocPhanBoSungAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />
        )}
        </>
    );
};

export default DuyetHocPhanBoSung;
